import { useState } from 'react';

import { useCashPosition, type CashAccountBalance } from '@/api/cash';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass } from '@/components/Field';
import { hueClasses } from '@/components/hues';
import { accountHue } from '@core/cash/board';
import { closeTheDay } from '@core/cash/closeDay';
import { kindIcon } from './accountIcons';
import {
  aboveFloat,
  afterMove,
  emptyTransfer,
  movableAccounts,
  moveLabel,
  otherBranches,
  overdrawWarning,
  sendRequest,
  transferAmount,
  transferProblem,
  transferRequest,
  type CreateCashTransferInput,
  type SendCashTransferInput,
  type TransferDraft,
} from '@core/cash/transfer';
import { formatAmount } from '@core/format/money';
import type { Session } from '@core/session/types';

const rupees = (value: number) => `₹${formatAmount(value)}`;

/** What the page asks the cash book to do: move within this branch, or send to another. */
export type MoveRequest =
  { kind: 'move'; input: CreateCashTransferInput } | { kind: 'send'; input: SendCashTransferInput };

/**
 * Move money (Cash & bank step 3) — the till's takings into the bank, cash drawn for the till, petty
 * cash topped up. The contra voucher, `docs/Cash_Bank_Flow_Design.md` §3.3; direction A's page.
 *
 * <p>
 * <strong>A page, not a drawer</strong>, reached by internal state from the cash book. It reads the
 * whole-history position itself rather than the cash book's window, because the balances beside
 * each account must be what is in it now, whatever period the cash book was showing.
 * </p>
 *
 * <p>
 * <strong>Moving more than the book shows is said, not refused</strong> (owner, 2026-10-22): the
 * notes are in the clerk's hand, and the gap is a missing receipt.
 * </p>
 */
export function MoveMoneyPage({
  session,
  today,
  initial,
  busy = false,
  onCancel,
  onSave,
}: {
  session: Session;
  /** The shop's date, `YYYY-MM-DD`. */
  today: string;
  /** A move already worked out — "deposit the rest" after a count. */
  initial?: Partial<TransferDraft> | undefined;
  busy?: boolean;
  onCancel: () => void;
  onSave: (request: MoveRequest) => void;
}) {
  const position = useCashPosition(session.branch?.id);
  const accounts = position.data?.accounts;

  if (!accounts) {
    return (
      <div className="flex flex-col gap-3 lg:gap-4">
        <PageHeading title="Move money" subtitle="Loading the accounts…" />
      </div>
    );
  }

  return (
    <MoveMoneyForm
      session={session}
      accounts={accounts}
      today={today}
      initial={initial}
      busy={busy}
      onCancel={onCancel}
      onSave={onSave}
    />
  );
}

function MoveMoneyForm({
  session,
  accounts,
  today,
  initial,
  busy,
  onCancel,
  onSave,
}: {
  session: Session;
  accounts: CashAccountBalance[];
  today: string;
  initial: Partial<TransferDraft> | undefined;
  busy: boolean;
  onCancel: () => void;
  onSave: (request: MoveRequest) => void;
}) {
  const firstField = useAutoFocusFirstField<HTMLButtonElement>();
  const [draft, setDraft] = useState<TransferDraft>(() => ({
    ...emptyTransfer(today, accounts),
    ...initial,
  }));
  const change = (next: Partial<TransferDraft>) => setDraft((was) => ({ ...was, ...next }));

  const movable = movableAccounts(accounts);
  const from = movable.find((a) => a.id === draft.fromId);
  const amount = transferAmount(draft.amount);
  const problem = transferProblem(draft, accounts, today);
  const warning = overdrawWarning(draft, accounts);
  const keep = aboveFloat(from);
  const lines = afterMove(draft, accounts);
  const branches = otherBranches(session);
  const sendingTo = branches.find((branch) => branch.id === draft.toBranchId);

  const save = () => {
    if (draft.toBranchId !== null) {
      const input = sendRequest(draft, accounts, today);

      if (input) {
        onSave({ kind: 'send', input });
      }

      return;
    }

    const input = transferRequest(draft, accounts, today);

    if (input) {
      onSave({ kind: 'move', input });
    }
  };

  // The evening's own moves, worked out as Close the day works them — the deposit and the top-up.
  const suggestions = closeTheDay({
    accounts,
    counts: undefined,
    today,
    dayBookPrinted: true,
    currency: '₹',
  })
    .filter((step) => step.move !== null)
    .map((step) => ({ key: step.id, title: step.title, line: step.line, move: step.move ?? {} }));

  const suggested = (move: Partial<TransferDraft>) =>
    draft.fromId === move.fromId &&
    draft.toId === move.toId &&
    draft.toBranchId === null &&
    draft.amount === move.amount;

  const tile = (on: boolean) =>
    `flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-start disabled:cursor-not-allowed ${
      on
        ? 'border-primary bg-primary-transparent border-2'
        : 'border-border-color hover:bg-light border bg-white'
    }`;

  return (
    <div {...enterKeyScope}>
      <PageHeading
        eyebrow="Cash & bank"
        title="Move money"
        subtitle="Neither income nor spending. It only changes where the money sits."
        overlap
        actions={
          <>
            <Button onClick={onCancel} disabled={busy} shortcut="back">
              Cancel
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              data-enter-key-commit
              shortcut="save"
              loading={busy}
              disabled={problem !== null || busy}
              onClick={save}
            >
              {sendingTo && amount !== null ? `Send ${rupees(amount)}` : moveLabel(amount)}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-12 items-start gap-4 px-4">
        <Card className="col-span-12 flex flex-col gap-5 xl:col-span-8">
          <div className="grid items-stretch gap-3.5 md:grid-cols-[minmax(0,1fr)_40px_minmax(0,1fr)]">
            <fieldset>
              <legend className="mb-2 text-[13px] font-bold tracking-[0.08em] text-gray-900 uppercase">
                From
              </legend>
              <div className="flex flex-col gap-2">
                {movable.map((account, index) => (
                  <button
                    key={account.id}
                    ref={index === 0 ? firstField : undefined}
                    type="button"
                    disabled={busy}
                    aria-pressed={draft.fromId === account.id}
                    onClick={() =>
                      change({
                        fromId: account.id,
                        // The far end cannot be the same account; it moves on to another one.
                        toId:
                          draft.toId === account.id
                            ? (movable.find((a) => a.id !== account.id)?.id ?? null)
                            : draft.toId,
                      })
                    }
                    className={tile(draft.fromId === account.id)}
                  >
                    <AccountMark account={account} />
                    <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-gray-900">
                      {account.name}
                    </span>
                    <span className="tabular-nums text-[13px] text-gray-900">
                      {rupees(account.closing)}
                    </span>
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="flex items-center justify-center" aria-hidden="true">
              <span className="bg-primary flex size-10 items-center justify-center rounded-full text-white">
                <i className="icon icon-arrow-right max-md:rotate-90" />
              </span>
            </div>

            <fieldset>
              <legend className="mb-2 text-[13px] font-bold tracking-[0.08em] text-gray-900 uppercase">
                To
              </legend>
              <div className="flex flex-col gap-2">
                {movable.map((account) =>
                  account.id === draft.fromId ? (
                    // Where the money leaves cannot also be where it lands: shown, not offered.
                    <div
                      key={account.id}
                      className="border-border-color bg-light flex items-center gap-2.5 rounded-xl border border-dashed px-3 py-2.5 opacity-60"
                    >
                      <AccountMark account={account} />
                      <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-gray-900">
                        {account.name}
                      </span>
                      <span className="text-default text-[12px]">Moving from here</span>
                    </div>
                  ) : (
                    <button
                      key={account.id}
                      type="button"
                      disabled={busy}
                      aria-pressed={draft.toBranchId === null && draft.toId === account.id}
                      onClick={() => change({ toBranchId: null, toId: account.id })}
                      className={tile(draft.toBranchId === null && draft.toId === account.id)}
                    >
                      <AccountMark account={account} />
                      <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-gray-900">
                        {account.name}
                      </span>
                      <span className="tabular-nums text-default text-[12px]">
                        {rupees(account.closing)}
                      </span>
                    </button>
                  ),
                )}
                {branches.map((branch) => (
                  <button
                    key={branch.id}
                    type="button"
                    disabled={busy}
                    aria-pressed={draft.toBranchId === branch.id}
                    onClick={() => change({ toBranchId: branch.id, toId: null })}
                    className={tile(draft.toBranchId === branch.id)}
                  >
                    <span
                      className="bg-success-transparent text-success flex size-9 shrink-0 items-center justify-center rounded-[10px]"
                      aria-hidden="true"
                    >
                      <i className="icon icon-store" />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-gray-900">
                      {branch.name}
                    </span>
                    <span className="text-default text-[12px]">Another branch</span>
                  </button>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="flex flex-col gap-2">
            <p className="mb-0 text-sm font-semibold text-gray-900">
              Amount <span className="text-danger">*</span>
            </p>
            {suggestions.length > 0 && (
              <div role="group" aria-label="Suggested amounts" className="flex flex-wrap gap-2">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.key}
                    type="button"
                    disabled={busy}
                    aria-pressed={suggested(suggestion.move)}
                    onClick={() => change({ ...suggestion.move, toBranchId: null })}
                    className={`cursor-pointer rounded-xl border px-3.5 py-2 text-start disabled:cursor-not-allowed ${
                      suggested(suggestion.move)
                        ? 'border-primary bg-primary text-white'
                        : 'border-border-color hover:bg-light bg-white text-gray-900'
                    }`}
                  >
                    <span className="block text-[13px] font-semibold">
                      {suggestion.title} · {rupees(Number(suggestion.move.amount))}
                    </span>
                    <span className="block text-[12px] opacity-85">{suggestion.line}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="border-primary flex h-14 w-full max-w-[360px] items-center gap-2 rounded-[10px] border-2 bg-white px-3.5">
                <span className="text-default font-serif text-[24px]" aria-hidden="true">
                  ₹
                </span>
                <input
                  // No type: the template pins every typed input to its small size with !important.
                  inputMode="decimal"
                  aria-label="Amount"
                  disabled={busy}
                  value={draft.amount}
                  onChange={(event) => change({ amount: event.target.value })}
                  className="min-w-0 flex-1 border-0 bg-transparent font-serif text-[26px] font-semibold text-gray-900 lining-nums tabular-nums outline-none"
                />
              </span>
              {keep !== null && from?.floatToKeep != null && (
                <Button disabled={busy} onClick={() => change({ amount: String(keep) })}>
                  Keep {rupees(from.floatToKeep)} float
                </Button>
              )}
              {from && from.closing > 0 && (
                <Button disabled={busy} onClick={() => change({ amount: String(from.closing) })}>
                  All {rupees(from.closing)}
                </Button>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Day" required hint="The day the money moved. It cannot be in the future.">
              <input
                type="date"
                max={today}
                disabled={busy}
                value={draft.sentOn}
                onChange={(event) => change({ sentOn: event.target.value })}
                className={controlClass}
              />
            </Field>
            <Field label="Slip number" hint="The bank's deposit slip or the UTR.">
              <input
                type="text"
                disabled={busy}
                value={draft.reference}
                onChange={(event) => change({ reference: event.target.value })}
                className={controlClass}
              />
            </Field>
          </div>

          {problem && (
            <p className="text-warning mb-0 text-[length:var(--scale-list-meta)]">{problem}</p>
          )}
        </Card>

        <aside aria-label="What it does" className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          <Card flush className="overflow-hidden">
            <div className="bg-primary-transparent border-primary/40 flex items-baseline justify-between border-b border-dashed px-5 py-3.5">
              <span className="text-primary text-[12px] font-bold tracking-[0.12em] uppercase">
                Contra voucher
              </span>
              <span className="text-primary text-[12px]">CT · numbered on save</span>
            </div>
            <div className="flex flex-col gap-3 px-5 py-4">
              <div className="flex items-center justify-between gap-2 text-[13px]">
                <span className="text-default">
                  Out of <span className="font-semibold text-gray-900">{from?.name ?? '—'}</span>
                </span>
                <span className="text-danger tabular-nums font-semibold">
                  {amount !== null ? `−${rupees(amount)}` : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 text-[13px]">
                <span className="text-default">
                  Into{' '}
                  <span className="font-semibold text-gray-900">
                    {sendingTo?.name ?? movable.find((a) => a.id === draft.toId)?.name ?? '—'}
                  </span>
                </span>
                <span
                  className={`tabular-nums font-semibold ${sendingTo ? 'text-warning' : 'text-success'}`}
                >
                  {sendingTo ? 'in transit' : amount !== null ? `+${rupees(amount)}` : '—'}
                </span>
              </div>
              {lines.length > 0 && (
                <dl className="border-border-color mb-0 flex flex-col gap-1.5 border-t pt-3">
                  {lines.map((line) => (
                    <div key={line.name} className="flex items-baseline justify-between gap-2">
                      <dt className="text-default text-[13px] font-normal">{line.name} after</dt>
                      <dd className="tabular-nums mb-0 text-[13px] font-semibold text-gray-900">
                        {rupees(line.after)}
                      </dd>
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between gap-2">
                    <dt className="text-default text-[13px] font-normal">Money in or out today</dt>
                    <dd className="mb-0 text-[13px] font-semibold text-gray-900">unchanged</dd>
                  </div>
                </dl>
              )}
            </div>
          </Card>

          {sendingTo && (
            <Notice tone="warning" title={`On its way to ${sendingTo.name}`}>
              It leaves this branch today and shows as in transit at both until {sendingTo.name}{' '}
              presses Received — so their drawer is never counted short by money still on the road.
              They choose where it lands. Until then it can be called off here.
            </Notice>
          )}

          {warning && (
            <Notice tone="warning" title="More than the book shows">
              {warning}
            </Notice>
          )}
        </aside>
      </div>
    </div>
  );
}

/** An account's tile, in its kind's hue. */
function AccountMark({ account }: { account: CashAccountBalance }) {
  const hue = hueClasses[accountHue[account.kind]];

  return (
    <span
      className={`flex size-9 shrink-0 items-center justify-center rounded-[10px] ${hue.wash} ${hue.ink}`}
      aria-hidden="true"
    >
      <i className={kindIcon[account.kind]} />
    </span>
  );
}
